import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  Box,
  MenuItem,
  MenuList,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { useInspect } from '@/designs-dev/inspect/InspectContext';
import {
  baseName,
  buildLayerTree,
  type LayerNode,
} from '@/designs-dev/inspect/fiber';
import {
  classifyComponent,
  componentRole,
} from '@/designs-dev/inspect/componentStatus';
import { deriveThemeStatus } from '@/designs-dev/inspect/themeStatus';

// Collapse layout primitives (Stack/Box/Grid doing layout) out of the tree,
// lifting their children up so the design-system components are kept — you see
// just the components, not the scaffolding holding them. Recurses, so nested
// layout wrappers collapse too.
const stripLayout = (nodes: LayerNode[]): LayerNode[] =>
  nodes.flatMap((n) =>
    n.inspection.isLayout
      ? stripLayout(n.children)
      : [{ ...n, children: stripLayout(n.children) }],
  );

// Light fingerprint of the tree so the rebuild interval only re-renders when
// the structure actually changed.
const signature = (nodes: LayerNode[]): string =>
  nodes
    .map(
      (n) =>
        `${n.id}${n.inspection.name}${n.origin?.prop ?? ''}${
          n.inspection.hasVisualSx ? '!' : ''
        }${
          n.inspection.layoutProps?.map((p) => `${p.name}${p.value}`).join() ??
          ''
        }(${signature(n.children)})`,
    )
    .join('|');

// Does the committed selection live anywhere in this node's subtree? Used to
// auto-open a row's ancestors when a component is picked on the screen, so the
// selected layer is revealed even though the tree starts collapsed.
const subtreeHas = (node: LayerNode, domNode: HTMLElement | null): boolean =>
  !!domNode &&
  (node.inspection.domNode === domNode ||
    node.children.some((c) => subtreeHas(c, domNode)));

// A broadcast from the tree root to open or close every row at once (the `\`
// shortcut). `seq` increments per press so re-pressing the same direction still
// re-applies — reopening rows that auto-revealed a selection in between.
type ExpandCmd = { open: boolean; seq: number } | null;

// How many times each visual sx look appears in the tree, so a second use can
// be told from a one-off.
const countVisualSx = (
  nodes: LayerNode[],
  counts = new Map<string, number>(),
): Map<string, number> => {
  for (const n of nodes) {
    const key = n.inspection.visualSxKey;
    if (key) counts.set(key, (counts.get(key) ?? 0) + 1);
    countVisualSx(n.children, counts);
  }
  return counts;
};

const Row = ({
  node,
  depth = 0,
  expandCmd,
  sxCounts,
}: {
  node: LayerNode;
  depth?: number;
  expandCmd: ExpandCmd;
  sxCounts: Map<string, number>;
}) => {
  const { current, setCurrent, setHovered } = useInspect();
  // Start collapsed — open a layer by clicking it (or its caret). Nested layers
  // stay hidden until you drill in.
  const [collapsed, setCollapsed] = useState(true);
  const ref = useRef<HTMLLIElement>(null);
  const theme = useTheme();

  // Expand/collapse-all: follow the broadcast whenever a new one lands.
  useEffect(() => {
    if (!expandCmd) return;
    setCollapsed(!expandCmd.open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandCmd?.seq]);

  const selected =
    !!current?.domNode &&
    current.domNode === node.inspection.domNode &&
    current.name === node.inspection.name;

  // Reveal a selection made on the screen: if it lands inside this row's
  // subtree, open the row so the highlighted layer isn't buried.
  //
  // And open the selected row itself, not only its ancestors. Picking a
  // component on the screen is a request to see what it is made of, so leaving
  // it shut meant every selection needed a second click on its own caret.
  const subtreeSelected = node.children.some((c) =>
    subtreeHas(c, current?.domNode ?? null),
  );
  useEffect(() => {
    if (subtreeSelected || selected) setCollapsed(false);
  }, [subtreeSelected, selected]);

  const base = baseName(node.inspection.name);
  // A prototype-owned component llama has no equivalent for — bespoke, not in
  // the design system (yet). Flagged with a green dot, the mirror of the coral
  // visual-sx dot.
  const provenance = classifyComponent(base);
  const isNew = provenance?.kind === 'new';
  // Forks a llama component of the same name: a cherry dot, with what
  // diverged at the prop level in its title. Reconcile it with llama.
  const drift = provenance?.kind === 'drift' ? provenance.note : null;
  // Changed in the prototype theme and not on main yet: a token llama doesn't
  // set, or a variant it doesn't declare. A warmOrange dot (decide on this),
  // distinct from the coral visual sx (not allowed).
  const hasProposedThemeChange = deriveThemeStatus(base)?.status === 'proposed';

  // A leading icon per layer category (à la Figma), so the tree reads at a
  // glance: a frame for a layout/layer, a puzzle piece for a feature component,
  // and a diamond for everything else — a wrapped DS component or a pipe-through
  // MUI one share the diamond, since here they're both just "a component".
  const role = componentRole(base);
  // Visual sx is fine on a feature component, or on a Box or Stack in one, as
  // long as it's a one-off. It's flagged coral when it restyles a Llama
  // component (MUI, or one from components/), which wants a variant instead,
  // or when the same look turns up a second time, which wants promoting.
  const sxUses = node.inspection.visualSxKey
    ? (sxCounts.get(node.inspection.visualSxKey) ?? 1)
    : 0;
  const onLlama =
    !node.inspection.isLayout &&
    role !== 'feature' &&
    provenance?.kind !== 'scaffold';
  const sxFlag = !node.inspection.hasVisualSx
    ? null
    : sxUses > 1
      ? `The same visual sx is used ${sxUses} times on this screen: make it a theme variant or a Llama component`
      : onLlama
        ? 'Visual sx on a Llama component: give it a variant, or make this a feature component'
        : null;
  const isLayer = node.inspection.isLayout || role === 'layout';
  const isFeature = role === 'feature';
  const typeIcon = isLayer
    ? 'vector-square'
    : isFeature
      ? 'puzzle-piece'
      : 'diamond';
  const typeColor = selected
    ? 'inherit'
    : isLayer
      ? 'custom.neutral.400'
      : isFeature
        ? // Lighter than primary.main so a feature still reads as distinct from
          // a design-system component now both come from the marine green ramp.
          'custom.marineGreen.300'
        : 'primary.main';

  // Selections carry slot provenance into the panel.
  const inspection = node.origin
    ? { ...node.inspection, origin: node.origin }
    : node.inspection;

  // Keep the selected row in view when the selection changes elsewhere.
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  return (
    <>
      <MenuItem
        ref={ref}
        dense
        selected={selected}
        // Hover only previews (highlights on the screen); it never changes the
        // committed selection. Click commits it (and toggles off if re-clicked).
        onMouseEnter={() => setHovered(inspection)}
        onClick={() => {
          setCollapsed(false);
          setCurrent(selected ? null : inspection);
        }}
        // The active row (the one whose details display below) gets its state
        // as an inline style — element-level, so no stylesheet rule (MUI's
        // hover/selected included) can override it.
        style={
          selected
            ? {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
              }
            : undefined
        }
        sx={{ pl: 1, borderRadius: '6px' }}
      >
        <Stack
          sx={{ alignItems: 'center', minWidth: 0 }}
          direction='row'
          spacing={0.75}
        >
          <Typography
            variant='caption'
            color={selected ? 'inherit' : 'text.secondary'}
            onClick={(e) => {
              e.stopPropagation();
              setCollapsed(!collapsed);
            }}
            sx={{
              width: 12,
              textAlign: 'center',
              cursor: 'pointer',
              visibility: node.children.length > 0 ? 'visible' : 'hidden',
              userSelect: 'none',
            }}
          >
            {collapsed ? '▸' : '▾'}
          </Typography>
          {/* type icon — frame / text / image / component-diamond */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
              color: typeColor,
            }}
          >
            <Icon icon={typeIcon as never} fontSize={14} />
          </Box>
          <Typography
            variant='body2'
            noWrap
            // Top-level layers read bold, nested ones lighter — depth as
            // hierarchy, the way Figma weights its layers panel.
            // Layout primitives read as subdued scaffolding (by colour) so the
            // DS components stay the focus.
            color={
              !node.inspection.isLayout || selected
                ? undefined
                : 'text.secondary'
            }
          >
            {node.inspection.name}
          </Typography>
          {/* slot provenance ("via children" / "via <prop>") is intentionally
              not shown per-layer — it's surfaced in the panel header when a
              layer is selected, so repeating it on every node is just noise. */}
          {sxFlag && (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'custom.coral.500',
                flexShrink: 0,
              }}
              title={sxFlag}
            />
          )}
          {isNew && (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'success.main',
                flexShrink: 0,
              }}
              title='New component — not in the design system yet'
            />
          )}
          {drift && (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'custom.cherry.500',
                flexShrink: 0,
              }}
              title={drift}
            />
          )}
          {hasProposedThemeChange && (
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'custom.warmOrange.500',
                flexShrink: 0,
              }}
              title='Proposed theme change — not on main yet'
            />
          )}
        </Stack>
      </MenuItem>
      {!collapsed && node.children.length > 0 && (
        <Box
          sx={{
            ml: 1.5,
            pl: 0.5,
            borderLeft: 1,
            borderColor: 'custom.neutral.200',
          }}
          // Indent the children behind a guide rail so they read clearly as
          // nested inside this component (e.g. the pieces within a Card).
        >
          {node.children.map((c) => (
            <Row
              key={c.id}
              node={c}
              depth={depth + 1}
              expandCmd={expandCmd}
              sxCounts={sxCounts}
            />
          ))}
        </Box>
      )}
    </>
  );
};

// Figma-style layers tree of the screen currently in the phone. Reads the same
// fiber tree as the hover inspector; hovering a row selects (and highlights)
// the component, clicking pins it so the details below stay put.
const LayersTree = ({ showLayout }: { showLayout: boolean }) => {
  const { active, screenEl, setHovered } = useInspect();
  const location = useLocation();
  const [tree, setTree] = useState<LayerNode[]>([]);
  // `\` toggles every layer open/closed at once. One command broadcast to all
  // rows; the direction alternates each press (first press expands).
  const [expandCmd, setExpandCmd] = useState<ExpandCmd>(null);

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '\\' || e.metaKey || e.ctrlKey || e.altKey) return;
      // Don't hijack the key while typing into a field on the prototype screen.
      const el = e.target as HTMLElement | null;
      const tag = el?.tagName;
      if (
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        el?.isContentEditable
      )
        return;
      e.preventDefault();
      setExpandCmd((prev) => ({
        open: prev ? !prev.open : true,
        seq: (prev?.seq ?? 0) + 1,
      }));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active]);

  useEffect(() => {
    if (!active || !screenEl) {
      setTree([]);
      return;
    }
    let sig: string | null = null;
    const rebuild = () => {
      const t = buildLayerTree(screenEl);
      const s = signature(t);
      if (s !== sig) {
        sig = s;
        setTree(t);
      }
    };
    rebuild();
    // Screens re-render as you interact (sheets open, tabs change) — poll
    // cheaply and re-render only when the signature moves.
    const interval = setInterval(rebuild, 1000);
    return () => clearInterval(interval);
  }, [active, screenEl, location.pathname]);

  if (tree.length === 0) {
    return (
      <Typography variant='caption' color='text.secondary'>
        No design-system components on this screen.
      </Typography>
    );
  }

  const displayTree = showLayout ? tree : stripLayout(tree);
  // Counted over the whole tree, so hiding layout doesn't hide a repeat.
  const sxCounts = countVisualSx(tree);

  return (
    <Box
      sx={{ height: '100%', minHeight: 0, overflow: 'auto' }}
      onMouseLeave={() => setHovered(null)}
    >
      {displayTree.length === 0 ? (
        <Typography variant='caption' color='text.secondary'>
          Only layout containers on this screen — nothing but scaffolding to
          show with layout hidden.
        </Typography>
      ) : (
        <MenuList dense disablePadding>
          {displayTree.map((n) => (
            <Row
              key={n.id}
              node={n}
              expandCmd={expandCmd}
              sxCounts={sxCounts}
            />
          ))}
        </MenuList>
      )}
    </Box>
  );
};

export default LayersTree;
